import * as React from "react";
import { useNavigate, useParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import {
  CmsBlock,
  CmsSlug,
  SafeHref,
  type AdminPage,
  type AdminPageSummary,
  type CmsKind,
} from "@poji/shared";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CheckboxField, Input, Label, NativeSelect, Textarea } from "@/components/ui/input";
import { ActionDialog } from "@/components/action-dialog";
import { BackLink } from "@/components/back-link";
import { DataTable } from "@/components/data-table";
import { ErrorState, PageHeader, errorText } from "@/components/common";
import { api, newIdempotencyKey } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { dateTime } from "@/lib/format";

/**
 * Sayfalar (P-16, K-141): hukuki, yardım ve güven sayfaları. Güvenli bloklarla yazılır (ham HTML yok);
 * yayınlanınca sitede /{dil}/p/{adres} adresinde görünür. "terms", "privacy", "cookies" adresleri yasal sayfalara bağlanır.
 */
const KIND: Record<CmsKind, string> = {
  page: "Sayfa",
  help_article: "Yardım yazısı",
  blog_post: "Blog yazısı",
};
const STATUS: Record<AdminPageSummary["status"], [string, "success" | "secondary" | "warning"]> = {
  draft: ["Taslak", "secondary"],
  in_review: ["İncelemede", "warning"],
  scheduled: ["Planlandı", "warning"],
  published: ["Yayında", "success"],
  archived: ["Arşiv", "secondary"],
};

const slugFrom = (s: string) =>
  s
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 80);

function NewPageDialog() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [f, setF] = React.useState({ kind: "page" as CmsKind, title: "", slug: "" });
  const slug = f.slug || slugFrom(f.title);
  return (
    <ActionDialog
      onOpen={() => setF({ kind: "page", title: "", slug: "" })}
      trigger={
        <Button size="sm">
          <Plus /> Yeni sayfa
        </Button>
      }
      title="Yeni sayfa"
      description="İlk içerik İngilizce yazılır. Adres küçük harf, rakam ve tireden oluşur; sonradan değiştirilebilir."
      confirmLabel="Oluştur"
      canSubmit={f.title.trim().length > 1 && CmsSlug.safeParse(slug).success}
      success="Sayfa oluşturuldu."
      onSubmit={async (key) => {
        const r = await api<{ id: string }>("/v1/admin/pages", {
          method: "POST",
          idempotencyKey: key,
          body: { kind: f.kind, locale: "en", title: f.title.trim(), slug },
        });
        await qc.invalidateQueries({ queryKey: ["admin-pages"] });
        void navigate(`/pages/${r.id}`);
      }}
    >
      <div className="grid gap-4">
        <div className="grid gap-2">
          <Label htmlFor="np-kind">Tür</Label>
          <NativeSelect
            id="np-kind"
            value={f.kind}
            onChange={(e) => setF({ ...f, kind: e.target.value as CmsKind })}
          >
            <option value="page">Sayfa (hukuki, güven, hakkımızda…)</option>
            <option value="help_article">Yardım yazısı</option>
            <option value="blog_post">Blog yazısı</option>
          </NativeSelect>
        </div>
        <div className="grid gap-2">
          <Label htmlFor="np-title">Başlık</Label>
          <Input
            id="np-title"
            value={f.title}
            onChange={(e) => setF({ ...f, title: e.target.value })}
          />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="np-slug">Adres</Label>
          <Input
            id="np-slug"
            value={f.slug}
            placeholder={slugFrom(f.title) || "terms"}
            onChange={(e) => setF({ ...f, slug: e.target.value })}
          />
          <p className="text-xs text-muted-foreground">Sitede: /en/p/{slug || "…"}</p>
        </div>
      </div>
    </ActionDialog>
  );
}

export function CmsPagesPage() {
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ["admin-pages"],
    queryFn: () => api<{ pages: AdminPageSummary[] }>("/v1/admin/pages"),
  });
  const columns: ColumnDef<AdminPageSummary, unknown>[] = [
    {
      id: "title",
      header: "Sayfa",
      cell: ({ row }) => {
        const en =
          row.original.translations.find((t) => t.locale === "en") ?? row.original.translations[0];
        return (
          <div>
            <div className="font-medium">{en?.title ?? "(başlıksız)"}</div>
            <div className="text-xs text-muted-foreground">/{en?.slug}</div>
          </div>
        );
      },
    },
    { id: "kind", header: "Tür", cell: ({ row }) => KIND[row.original.kind] },
    {
      id: "langs",
      header: "Diller",
      cell: ({ row }) => row.original.translations.map((t) => t.locale).join(", "),
    },
    {
      id: "status",
      header: "Durum",
      cell: ({ row }) => {
        const [t, v] = STATUS[row.original.status];
        return <Badge variant={v}>{t}</Badge>;
      },
    },
    {
      id: "updated",
      header: "Güncellendi",
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">{dateTime(row.original.updatedAt)}</span>
      ),
    },
  ];
  return (
    <>
      <PageHeader
        title="Sayfalar"
        description="Hukuki metinler, yardım ve güven sayfaları. Yayınlanan sayfa sitede /{dil}/p/{adres} adresinde görünür; 'terms', 'privacy' ve 'cookies' adresleri Şartlar, Gizlilik ve Çerez sayfalarının yerine geçer."
        actions={<NewPageDialog />}
      />
      <DataTable
        columns={columns}
        data={q.data?.pages}
        isLoading={q.isPending}
        error={q.error}
        onRetry={() => void q.refetch()}
        empty="Henüz sayfa yok."
        onRowClick={(r) => void navigate(`/pages/${r.id}`)}
      />
    </>
  );
}

// ------------------------------------------------------------------ düzenleyici

type Content = AdminPage["content"][string];
const emptyContent = (): Content => ({
  title: "",
  slug: "",
  excerpt: null,
  blocks: [],
  seoTitle: null,
  seoDescription: null,
  noindex: false,
  updatedAt: "",
});

const NEW_BLOCK: Record<CmsBlock["type"], [string, () => CmsBlock]> = {
  heading: ["Başlık", () => ({ type: "heading", level: 2, text: "" })],
  paragraph: ["Paragraf", () => ({ type: "paragraph", text: "" })],
  list: ["Liste", () => ({ type: "list", ordered: false, items: [""] })],
  button: ["Düğme", () => ({ type: "button", label: "", href: "/", style: "primary" })],
  faq: ["Sık sorulan sorular", () => ({ type: "faq", items: [{ q: "", a: "" }] })],
  callout: ["Bilgi kutusu", () => ({ type: "callout", tone: "info", text: "" })],
  divider: ["Ayırıcı çizgi", () => ({ type: "divider" })],
};

/** Kaydetmeden önce sorunları Türkçe listeler (boş alan, geçersiz adres) */
export function contentProblems(c: Content): string[] {
  const out: string[] = [];
  if (!c.title.trim()) out.push("Başlık boş.");
  if (!CmsSlug.safeParse(c.slug).success) out.push("Adres küçük harf, rakam ve tireden oluşmalı.");
  c.blocks.forEach((b, i) => {
    if (!CmsBlock.safeParse(b).success)
      out.push(`${i + 1}. blok (${NEW_BLOCK[b.type][0]}) eksik ya da hatalı.`);
    if (b.type === "button" && b.href && !SafeHref.safeParse(b.href).success)
      out.push(`${i + 1}. blok: adres https:// ya da / ile başlamalı.`);
  });
  return out;
}

function BlockEditor({ block, onChange }: { block: CmsBlock; onChange: (b: CmsBlock) => void }) {
  switch (block.type) {
    case "heading":
      return (
        <div className="flex gap-2">
          <NativeSelect
            aria-label="Düzey"
            className="w-32"
            value={block.level}
            onChange={(e) => onChange({ ...block, level: Number(e.target.value) as 2 | 3 })}
          >
            <option value={2}>Büyük</option>
            <option value={3}>Küçük</option>
          </NativeSelect>
          <Input
            aria-label="Başlık metni"
            value={block.text}
            onChange={(e) => onChange({ ...block, text: e.target.value })}
          />
        </div>
      );
    case "paragraph":
      return (
        <Textarea
          aria-label="Paragraf"
          rows={4}
          value={block.text}
          onChange={(e) => onChange({ ...block, text: e.target.value })}
        />
      );
    case "list":
      return (
        <div className="grid gap-2">
          <CheckboxField
            label="Numaralı liste"
            checked={block.ordered}
            onChange={(e) => onChange({ ...block, ordered: e.target.checked })}
          />
          <Textarea
            aria-label="Liste maddeleri"
            rows={4}
            placeholder="Her satır bir madde"
            value={block.items.join("\n")}
            onChange={(e) => onChange({ ...block, items: e.target.value.split("\n") })}
          />
        </div>
      );
    case "button":
      return (
        <div className="grid gap-2 sm:grid-cols-3">
          <Input
            aria-label="Düğme yazısı"
            placeholder="Yazı"
            value={block.label}
            onChange={(e) => onChange({ ...block, label: e.target.value })}
          />
          <Input
            aria-label="Düğme adresi"
            placeholder="/search ya da https://…"
            value={block.href}
            onChange={(e) => onChange({ ...block, href: e.target.value })}
          />
          <NativeSelect
            aria-label="Görünüm"
            value={block.style}
            onChange={(e) =>
              onChange({ ...block, style: e.target.value as "primary" | "secondary" })
            }
          >
            <option value="primary">Ana düğme</option>
            <option value="secondary">İkincil düğme</option>
          </NativeSelect>
        </div>
      );
    case "faq":
      return (
        <div className="grid gap-3">
          {block.items.map((it, i) => (
            <div key={i} className="grid gap-2 rounded-md border p-3">
              <Input
                aria-label={`Soru ${i + 1}`}
                placeholder="Soru"
                value={it.q}
                onChange={(e) =>
                  onChange({
                    ...block,
                    items: block.items.map((x, j) => (j === i ? { ...x, q: e.target.value } : x)),
                  })
                }
              />
              <Textarea
                aria-label={`Cevap ${i + 1}`}
                placeholder="Cevap"
                rows={3}
                value={it.a}
                onChange={(e) =>
                  onChange({
                    ...block,
                    items: block.items.map((x, j) => (j === i ? { ...x, a: e.target.value } : x)),
                  })
                }
              />
              <Button
                size="sm"
                variant="ghost"
                className="justify-self-end"
                onClick={() => onChange({ ...block, items: block.items.filter((_, j) => j !== i) })}
                disabled={block.items.length < 2}
              >
                <Trash2 /> Soruyu sil
              </Button>
            </div>
          ))}
          <Button
            size="sm"
            variant="outline"
            className="justify-self-start"
            onClick={() => onChange({ ...block, items: [...block.items, { q: "", a: "" }] })}
          >
            <Plus /> Soru ekle
          </Button>
        </div>
      );
    case "callout":
      return (
        <div className="grid gap-2">
          <div className="grid gap-2 sm:grid-cols-3">
            <NativeSelect
              aria-label="Kutu türü"
              value={block.tone}
              onChange={(e) => onChange({ ...block, tone: e.target.value as "info" | "warning" })}
            >
              <option value="info">Bilgi</option>
              <option value="warning">Uyarı</option>
            </NativeSelect>
            <Input
              aria-label="Kutu başlığı"
              className="sm:col-span-2"
              placeholder="Başlık (isteğe bağlı)"
              value={block.title ?? ""}
              onChange={(e) =>
                onChange({
                  type: "callout",
                  tone: block.tone,
                  text: block.text,
                  ...(e.target.value ? { title: e.target.value } : {}),
                })
              }
            />
          </div>
          <Textarea
            aria-label="Kutu metni"
            rows={3}
            value={block.text}
            onChange={(e) => onChange({ ...block, text: e.target.value })}
          />
        </div>
      );
    case "divider":
      return <hr className="my-2" />;
  }
}

export function CmsPageEditorPage() {
  const { id = "" } = useParams();
  const qc = useQueryClient();
  const { can } = useAuth();
  const q = useQuery({
    queryKey: ["admin-page", id],
    queryFn: () => api<AdminPage>(`/v1/admin/pages/${id}`),
  });
  const [locale, setLocale] = React.useState("en");
  const [draft, setDraft] = React.useState<Content | null>(null);
  const [msg, setMsg] = React.useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (q.data) setDraft(q.data.content[locale] ?? emptyContent());
  }, [q.data, locale]);

  if (q.error) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  if (!q.data || !draft) return <p className="text-sm text-muted-foreground">Yükleniyor…</p>;
  const page = q.data;
  const problems = contentProblems(draft);
  const set = (p: Partial<Content>) => setDraft({ ...draft, ...p });
  const setBlock = (i: number, b: CmsBlock) =>
    set({ blocks: draft.blocks.map((x, j) => (j === i ? b : x)) });
  const move = (i: number, d: -1 | 1) => {
    const bs = [...draft.blocks];
    const j = i + d;
    if (j < 0 || j >= bs.length) return;
    [bs[i], bs[j]] = [bs[j]!, bs[i]!];
    set({ blocks: bs });
  };
  const [statusText, statusVariant] = STATUS[page.status];

  async function run(fn: () => Promise<unknown>, ok: string) {
    setBusy(true);
    setMsg(null);
    try {
      await fn();
      await qc.invalidateQueries({ queryKey: ["admin-page", id] });
      await qc.invalidateQueries({ queryKey: ["admin-pages"] });
      setMsg({ ok: true, text: ok });
    } catch (e) {
      setMsg({ ok: false, text: errorText(e) });
    } finally {
      setBusy(false);
    }
  }
  const save = () =>
    run(
      () =>
        api(`/v1/admin/pages/${id}/${locale}`, {
          method: "PUT",
          body: {
            title: draft.title.trim(),
            slug: draft.slug,
            excerpt: draft.excerpt?.trim() || null,
            blocks: draft.blocks,
            seoTitle: draft.seoTitle?.trim() || null,
            seoDescription: draft.seoDescription?.trim() || null,
            noindex: draft.noindex,
          },
        }),
      "Kaydedildi.",
    );
  const publish = (on: boolean) =>
    run(
      () =>
        api(`/v1/admin/pages/${id}/${on ? "publish" : "unpublish"}`, {
          method: "POST",
          idempotencyKey: newIdempotencyKey(),
        }),
      on ? "Yayınlandı." : "Yayından kaldırıldı.",
    );

  return (
    <>
      <PageHeader
        back={<BackLink to="/pages">Sayfalar</BackLink>}
        title={
          <span className="flex items-center gap-2">
            {draft.title || "Yeni sayfa"} <Badge variant={statusVariant}>{statusText}</Badge>
          </span>
        }
        description={`${KIND[page.kind]} · sitede /${locale}/${page.kind === "blog_post" ? "blog" : "p"}/${draft.slug || "…"}${["terms", "privacy", "cookies"].includes(draft.slug) ? " ve /legal/" + draft.slug : ""}`}
        actions={
          <>
            <NativeSelect
              aria-label="Dil"
              value={locale}
              onChange={(e) => setLocale(e.target.value)}
            >
              {page.locales.map((l) => (
                <option key={l} value={l}>
                  {l === "en" ? "English" : l === "mt" ? "Malti" : l}
                  {page.content[l] ? "" : " (yeni)"}
                </option>
              ))}
            </NativeSelect>
            <Button onClick={() => void save()} disabled={busy || problems.length > 0}>
              Kaydet
            </Button>
            {can("cms.pages.publish") &&
              (page.status === "published" ? (
                <Button variant="outline" disabled={busy} onClick={() => void publish(false)}>
                  Yayından kaldır
                </Button>
              ) : (
                <Button
                  variant="outline"
                  disabled={busy || !page.content["en"]}
                  onClick={() => void publish(true)}
                >
                  Yayınla
                </Button>
              ))}
          </>
        }
      />
      {msg && (
        <p role="status" className={`mb-4 text-sm ${msg.ok ? "text-success" : "text-destructive"}`}>
          {msg.text}
        </p>
      )}
      {problems.length > 0 && (
        <ul className="mb-4 list-disc pl-5 text-sm text-muted-foreground">
          {problems.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      )}
      {locale !== "en" && !page.content[locale] && (
        <p className="mb-4 text-sm text-muted-foreground">
          Bu dilde henüz içerik yok. Kaydedince oluşur; yoksa site İngilizce sürümü gösterir.
        </p>
      )}
      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <div className="grid gap-4">
          <Card>
            <CardContent className="grid gap-4 p-4">
              <div className="grid gap-2">
                <Label htmlFor="pg-title">Başlık</Label>
                <Input
                  id="pg-title"
                  value={draft.title}
                  onChange={(e) => set({ title: e.target.value })}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="pg-excerpt">Kısa özet (isteğe bağlı)</Label>
                <Input
                  id="pg-excerpt"
                  value={draft.excerpt ?? ""}
                  onChange={(e) => set({ excerpt: e.target.value })}
                />
              </div>
            </CardContent>
          </Card>
          {draft.blocks.map((b, i) => (
            <Card key={i}>
              <CardContent className="grid gap-3 p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">
                    {i + 1}. {NEW_BLOCK[b.type][0]}
                  </span>
                  <span className="flex gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label="Yukarı taşı"
                      disabled={i === 0}
                      onClick={() => move(i, -1)}
                    >
                      <ArrowUp />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label="Aşağı taşı"
                      disabled={i === draft.blocks.length - 1}
                      onClick={() => move(i, 1)}
                    >
                      <ArrowDown />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label="Bloğu sil"
                      onClick={() => set({ blocks: draft.blocks.filter((_, j) => j !== i) })}
                    >
                      <Trash2 />
                    </Button>
                  </span>
                </div>
                <BlockEditor block={b} onChange={(nb) => setBlock(i, nb)} />
              </CardContent>
            </Card>
          ))}
          <div className="flex flex-wrap gap-2">
            {(Object.keys(NEW_BLOCK) as CmsBlock["type"][]).map((t) => (
              <Button
                key={t}
                size="sm"
                variant="outline"
                onClick={() => set({ blocks: [...draft.blocks, NEW_BLOCK[t][1]()] })}
              >
                <Plus /> {NEW_BLOCK[t][0]}
              </Button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            Metinde bağlantı için [yazı](/yol) ya da [yazı](https://…) yazın. Ham HTML desteklenmez.
          </p>
        </div>
        <Card className="h-fit">
          <CardContent className="grid gap-4 p-4">
            <div className="grid gap-2">
              <Label htmlFor="pg-slug">Adres</Label>
              <Input
                id="pg-slug"
                value={draft.slug}
                onChange={(e) => set({ slug: e.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="pg-seot">Arama sonucu başlığı</Label>
              <Input
                id="pg-seot"
                value={draft.seoTitle ?? ""}
                onChange={(e) => set({ seoTitle: e.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="pg-seod">Arama sonucu açıklaması</Label>
              <Textarea
                id="pg-seod"
                rows={3}
                value={draft.seoDescription ?? ""}
                onChange={(e) => set({ seoDescription: e.target.value })}
              />
            </div>
            <CheckboxField
              label="Arama motorlarında gösterme"
              checked={draft.noindex}
              onChange={(e) => set({ noindex: e.target.checked })}
            />
          </CardContent>
        </Card>
      </div>
    </>
  );
}
